import React, { useState, useRef } from 'react'
import LanguageTranslator from '../../locals/LanguageTranslator'
import TextWithTooltip from '../../genericComponents/TextWithTooltip'
import GenericAvatarComponent from '../../genericComponents/GenericAvatarComponent'
import { ChevronDown } from 'lucide-react'

const getFullName = (person) => [person?.firstName, person?.lastName].filter(Boolean).join(' ') || person?.username || '..'

// taskCount is a person's own tasks - their total also includes everyone under them, at every depth
const getSubtreeTotal = (member) => (Number(member?.taskCount) || 0)
    + (Array.isArray(member?.reportees) ? member.reportees.reduce((sum, reportee) => sum + getSubtreeTotal(reportee), 0) : 0)

// Only one level below a direct report is shown, so each row rolls up its whole team into "Team tasks"
const getMemberCounts = (member) => {
    const myCount = Number(member?.taskCount) || 0
    const total = getSubtreeTotal(member)
    return { myCount, teamCount: total - myCount, total }
}

const TeamTasksDropdown = (props) => {
    const { isOpen, onToggle, data, selectedOption, onSelect } = props

    // Team whose reportees are shown in the hover-only side panel, with its row's offset from the top of the dropdown
    const [hoveredMember, setHoveredMember] = useState(null)
    const dropdownRef = useRef(null)

    const personal = data?.personal || {}
    const teamMembers = Array.isArray(data?.team?.members) ? data.team.members : []
    const myTotal = Number(personal.taskCount) || 0
    const teamsTotal = Number(data?.team?.total) || 0
    const allTotal = myTotal + teamsTotal
    const selfCounts = { myCount: myTotal, teamCount: teamsTotal }
    const selectedMember = teamMembers.find((member) => String(member.jti) === String(selectedOption))
    const triggerLabel = selectedOption === 'all'
        ? `${LanguageTranslator({ children: 'All' })} (${allTotal})`
        : selectedMember
            ? `${getFullName(selectedMember)} (${getMemberCounts(selectedMember).total})`
            : `${LanguageTranslator({ children: 'My Tasks' })} (${myTotal})`

    const handleSelect = (option) => {
        setHoveredMember(null)
        onSelect(option)
    }

    const handleMemberHover = (event, member) => {
        if (!member.reportees?.length) return setHoveredMember(null)
        const rowTop = event.currentTarget.getBoundingClientRect().top
        const dropdownTop = dropdownRef.current?.getBoundingClientRect().top || 0
        setHoveredMember({ member, top: rowTop - dropdownTop })
    }

    const optionRowClass = (isSelected) => `w-full flex items-center gap-3 text-left px-4 py-3 ${isSelected ? 'bg-primary_w_less_opacity' : 'hover:bg-[#F8FAFC]'}`

    const renderTaskSplit = ({ myCount, teamCount }) => (
        <>
            <LanguageTranslator>My tasks</LanguageTranslator> <span className="font-semibold">({myCount})</span> + <LanguageTranslator>Team tasks</LanguageTranslator> <span className="font-semibold">({teamCount})</span>
        </>
    )

    const renderPersonText = (person, nameClassName, counts) => (
        <span className="flex-1 min-w-0">
            <span className={`block text-fontBlack ${nameClassName}`}>
                <TextWithTooltip as="span" width="100%" text={getFullName(person)} />
            </span>
            <span className="flex items-center gap-1.5 min-w-0 text-[11px] leading-[16px] text-fontSlateGrey">
                <span className="flex min-w-0">
                    <TextWithTooltip as="span" width="100%" text={person?.username || '..'} />
                </span>
                <span className="shrink-0">·</span>
                <span className="shrink-0 whitespace-nowrap">{renderTaskSplit(counts)}</span>
            </span>
        </span>
    )

    return (
        <>
            <button
                type="button"
                onClick={onToggle}
                className={`flex items-center justify-between w-full gap-1 !h-auto min-h-[36px] px-3 py-2 rounded-[6px] border border-solid text-[13px] leading-[18px] font-semibold text-fontBlack ${isOpen ? 'border-fontBlack' : 'border-borderSlateGrey hover:border-fontSlateGrey'}`}
            >
                <TextWithTooltip as="span" width="100%" text={triggerLabel} />
                <ChevronDown size={14} className={`shrink-0 text-fontSlateGrey transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div ref={dropdownRef} onMouseLeave={() => setHoveredMember(null)} className="absolute top-[calc(100%+6px)] left-4 z-[5] w-[400px]">
                    <div className="bg-white border border-borderSlateGrey shadow-xl rounded-[8px] overflow-hidden">
                        <button type="button" onMouseEnter={() => setHoveredMember(null)} onClick={() => handleSelect('all')} className={optionRowClass(selectedOption === 'all')}>
                            <span className="flex-1 min-w-0">
                                <span className="block text-[13px] font-semibold text-fontBlack"><LanguageTranslator>All tasks</LanguageTranslator></span>
                                <span className="block text-[11px] text-fontSlateGrey">{renderTaskSplit(selfCounts)}</span>
                            </span>
                            <span className="shrink-0 text-[13px] font-semibold text-fontBlack">{allTotal}</span>
                        </button>

                        <button type="button" onMouseEnter={() => setHoveredMember(null)} onClick={() => handleSelect('self')} className={`${optionRowClass(selectedOption === 'self')} ${teamMembers.length ? 'border-b border-borderSlateGrey' : ''}`}>
                            <GenericAvatarComponent name={getFullName(personal)} size={32} />
                            {renderPersonText(personal, 'text-[13px] font-semibold', selfCounts)}
                            <span className="shrink-0 text-[13px] font-semibold text-fontBlack">{allTotal}</span>
                        </button>

                        {!!teamMembers.length && (
                            <>
                                <p onMouseEnter={() => setHoveredMember(null)} className="mb-0 px-4 pt-3 pb-1.5 text-[10px] font-semibold text-fontSlateGrey tracking-wide">
                                    <LanguageTranslator>MY TEAMS</LanguageTranslator>
                                </p>
                                <div onScroll={() => setHoveredMember(null)} className="max-h-[260px] overflow-y-auto">
                                    {teamMembers.map((member) => {
                                        const isSelected = String(member.jti) === String(selectedOption)
                                        const isHovered = String(hoveredMember?.member.jti) === String(member.jti)
                                        const counts = getMemberCounts(member)
                                        // No own tasks - nothing to show if picked, but the hover panel still opens for their team.
                                        // Not using `disabled`, since disabled buttons don't fire mouse events and the hover would break
                                        const isSelectable = counts.myCount > 0
                                        return (
                                            <button
                                                key={member.jti}
                                                type="button"
                                                aria-disabled={!isSelectable}
                                                onMouseEnter={(event) => handleMemberHover(event, member)}
                                                onClick={() => isSelectable && handleSelect(member.jti)}
                                                className={`${optionRowClass(isSelected)} border-b last:border-b-0 border-borderSlateGrey ${isHovered && !isSelected ? 'bg-[#F8FAFC]' : ''} ${isSelectable ? '' : '!cursor-not-allowed'}`}
                                            >
                                                <GenericAvatarComponent name={getFullName(member)} size={32} />
                                                {renderPersonText(member, 'text-[13px] font-semibold', counts)}
                                                <span className="shrink-0 text-[13px] font-semibold text-fontBlack">{counts.total}</span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Direct reportees of the hovered member - shown on hover only, rows are informational and not selectable */}
                    {hoveredMember && (
                        <div className="absolute left-full pl-1.5 w-[360px]" style={{ top: hoveredMember.top }}>
                            <div className="bg-white border border-borderSlateGrey shadow-xl rounded-[8px] py-1 max-h-[340px] overflow-y-auto">
                                {hoveredMember.member.reportees.map((reportee) => {
                                    const counts = getMemberCounts(reportee)
                                    return (
                                        <div key={reportee.jti} className="flex items-center gap-3 px-3 py-2 cursor-default">
                                            <GenericAvatarComponent name={getFullName(reportee)} size={28} />
                                            {renderPersonText(reportee, 'text-[12px] font-medium', counts)}
                                            <span className="shrink-0 text-[12px] font-semibold text-fontBlack">{counts.total}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

export default TeamTasksDropdown
